import { CAROUSEL_CARD, CONTENT_CARD, HOTSPOT, HTML, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER, MODAL, PAGE, TEXT } from '../constants/widgetTypes';
import { Frame } from 'scenejs';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { getAllAncestors, getBoundingRectFromLayout, getYForWidget, applyHeightChangesToAncestors } from '../components/widgets/widget.helper';

/**
 * Globally attachable widget types that support auto-grow.
 * These widgets can be attached to various parent containers and require
 * ancestor chain handling when their height changes.
 * Add new widget types here as they get auto-grow support.
 */
// TEXT: personalized (AI) text only; the text widget enables AutoGrowLayout for it.
export const GLOBALLY_ATTACHABLE_WIDGET_TYPES = [HTML, TEXT];

/**
 * Per-widget configuration for auto-grow behavior.
 * Add overrides when a globally attachable widget needs special handling
 * in certain container contexts.
 */
export const AUTO_GROW_WIDGET_OVERRIDES = {
  [HTML]: {
    // Skip container (page) height update when directly inside these parent types.
    // e.g. Content card manages its own layout, so we don't update page height.
    skipContainerUpdateWhenInside: [CONTENT_CARD],
    excludeFromAncestors: [PAGE],
  },
  [TEXT]: {
    // A hotspot popup floats over the page: growth inside it grows the popup and stops
    // there, so the widget it is attached to and the page under it do not move.
    stopAtAncestors: [HOTSPOT],
  },
};

export function getAutoGrowConfig(widget, widgetsById) {
  const isGloballyAttachable = GLOBALLY_ATTACHABLE_WIDGET_TYPES.includes(widget.type);
  const overrides = AUTO_GROW_WIDGET_OVERRIDES[widget.type] || {};
  const immediateParent = widgetsById[widget.parent];
  const skipContainerUpdateWhenInside = overrides.skipContainerUpdateWhenInside || [];
  const excludeFromAncestors = overrides.excludeFromAncestors || [];

  const shouldSkipContainerUpdate = isGloballyAttachable && skipContainerUpdateWhenInside.includes(immediateParent?.type);
  const excludeWidgets = shouldSkipContainerUpdate ? excludeFromAncestors : [];

  return {
    isGloballyAttachable,
    shouldSkipContainerUpdate,
    excludeWidgets,
    stopAtAncestors: overrides.stopAtAncestors || [],
  };
}

export function findContainerWidget(widget, widgetsById) {
  const parent = widgetsById[widget.parent];
  if (parent == null || [PAGE, MODAL, LIVE_ELEMENT_CONTAINER].includes(parent?.type)) {
    return parent ?? null;
  }
  return findContainerWidget(parent, widgetsById);
}

export function getImpactedWidgets({ widgets, autoGrowWidget, ancestorIdSet, breakpoint, referenceY, isGloballyAttachable }) {
  return widgets
    .filter(item => item.uid !== autoGrowWidget.uid)
    .filter(item =>
      isGloballyAttachable
        ? ancestorIdSet.has(item.parent)
        : item.parent === autoGrowWidget.parent
    )
    .filter(item => {
      const widgetsById = keyBy(widgets, 'uid');
      return getYForWidget(item, widgetsById, breakpoint) >= referenceY;
    });
}

export function getImpactedWidgetChanges({ impactedWidgets, breakpoint, delta }) {
  const changes = {};
  impactedWidgets.forEach(item => {
    const itemLayout = get(item, `properties.${breakpoint}.layout`);
    const itemRect = getBoundingRectFromLayout(itemLayout);
    const frame = new Frame(itemLayout);
    if (frame.has('transform', 'translate')) {
      frame.set('transform', 'translate', `${itemRect.x}px, ${itemRect.y + delta}px`);
    }
    changes[item.uid] = {
      id: item.uid,
      path: `properties.${breakpoint}.layout`,
      value: frame.get(),
    };
  });
  return changes;
}

export function getLiveElementParentChanges(containerWidget, parentWidgets, breakpoint, delta) {
  const liveElementByContainerId = {};
  for (const w of parentWidgets) {
    if (w.type === LIVE_ELEMENT && w.children) {
      for (const child of w.children) {
        if (child.id) liveElementByContainerId[child.id] = w;
      }
    }
  }
  const liveElement = liveElementByContainerId[containerWidget.id];
  if (!liveElement) return null;

  const parentWidgetsById = keyBy(parentWidgets, 'uid');
  const ancestors = getAllAncestors(liveElement, parentWidgets);
  const ancestorIdSet = new Set(ancestors.map(a => a.uid));
  const referenceY = getYForWidget(liveElement, parentWidgetsById);

  const ancestorChanges = applyHeightChangesToAncestors(
    ancestors,
    liveElement,
    breakpoint,
    delta
  );

  const impactedWidgets = getImpactedWidgets({
    widgets: parentWidgets,
    autoGrowWidget: liveElement,
    ancestorIdSet,
    breakpoint,
    referenceY,
    isGloballyAttachable: true
  });

  return {
    ...ancestorChanges,
    ...getImpactedWidgetChanges({ impactedWidgets, breakpoint, delta }),
  };
}

export const isLiveElementInsideCarousel = (containerWidget, parentWidgets) => {
  if (!containerWidget || !parentWidgets.length) return false;

  const liveElement = parentWidgets.find(w => 
    w.type === LIVE_ELEMENT && 
    w.children?.some(child => child.id === containerWidget.id)
  );

  if (!liveElement) return false;

  const ancestors = getAllAncestors(liveElement, parentWidgets);

  return ancestors.some(ancestor => ancestor.type === CAROUSEL_CARD);
};
